'use client';
import { memo, useEffect, useMemo, useRef, useState } from 'react';

import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useHydrateAtoms } from 'jotai/utils';
import { useRouter } from 'next/navigation';

import { filtersListAtom } from '@core/Atoms/Plp/FiltersListAtom';
import { postcodeAtom } from '@core/Atoms/Postcode/Postcode.atom';
import { aggregationsAtom, initialAggregationsState } from '@core/Atoms/Search/AggregationsAtom';
import type { AggregationsState } from '@core/Entities/Search/Aggregations/Aggregations.entity';
import type {
  FilterKey,
  Filter as FilterType,
} from '@core/Entities/Search/SearchFilters/Filter.entity';
import { createPathFromFilters } from '@core/Utils/Search/CreatePathFromFilters';
import { getFilterFromArray } from '@core/Utils/SearchFilters/GetFilterFromArray';
import { mapFiltersForDisplay } from '@core/Utils/SearchFilters/MapFiltersForDisplay';
import { useLockBodyScroll } from '@core/Utils/UseBodyLockOnScroll';
import { Accordion } from '@gds/Accordion/Accordion';
import Button from '@gds/Button/Button';
import { Close } from '@gds/Icons/Paths/Close';
import { Filter } from '@gds/Icons/Paths/Filter';
import { Trash } from '@gds/Icons/Paths/Trash';
import { Typography } from '@gds/Typography/Typography';
import { dynamicFilterMessagesAtom } from '@plp/Atoms/DynamicFilterMessagesAtom';
import { filterUpdateStartedAtom } from '@plp/Atoms/FilterUpdateStarted';
import { useGetRoot } from '@search/Hooks/UseGetRoot';
import { useInitAggregations } from '@search/Hooks/UseInitAggregations';
import { filterTrayOpenAtom } from '@search-filters/Atom/FilterTrayOpen.atom';
import { priceTypeAtom } from '@search-filters/Atom/PriceTypeAtom';

import { CANT_DELETE } from './Components/ActiveFiltersOverview/ActiveFiltersOverview.constants';
import { FILTER_CATEGORIES } from './FilterPanel.constants';
import type { FilterPanelProps, PlpFiltersMessagesProps } from './FilterPanel.entity';
import { trackFilterEvent } from './Filters.tracking';
import SearchAccordion from './SearchAccordion';

import styles from './FilterPanel.module.css';

export const FilterPanel = memo(
  ({
    flags,
    strings,
    tenant,
    enableFilterAnchors = true,
    initialAggregations,
    initialActiveFilters,
    children,
  }: FilterPanelProps & React.PropsWithChildren) => {
    useInitialValues({ initialAggregations, initialActiveFilters });
    const isRac = tenant === 'rac';
    const root = useGetRoot();
    const [filtersList, setFiltersList] = useAtom(filtersListAtom);
    const dynamicStrings = useAtomValue(dynamicFilterMessagesAtom);
    useInitAggregations();
    const router = useRouter();
    const savedPostcode = useAtomValue(postcodeAtom);
    const postcodeFilter = getFilterFromArray('postcode', filtersList);

    const filterUpdateStarted = useAtomValue(filterUpdateStartedAtom);
    const [isFiltersOpen, setIsFiltersOpen] = useAtom(filterTrayOpenAtom);
    const [isShowingAllFilters, setIsShowingAllFilters] = useState(false);
    const setPriceType = useSetAtom(priceTypeAtom);

    useLockBodyScroll(isFiltersOpen);

    const filterCategoriesIntl = FILTER_CATEGORIES.map(type => {
      const filterValue = getFilterFromArray(type.id as FilterKey, filtersList);
      const labelAddon = Array.isArray(filterValue?.value) ? ` (${filterValue?.value.length})` : '';
      const isDisabled =
        !!(type?.parentId && !getFilterFromArray(type.parentId as FilterKey, filtersList)) ||
        (type.id !== 'location' && !savedPostcode && !postcodeFilter);

      const isHidden =
        (type.id === 'variant-type' && !getFilterFromArray('make', filtersList)) ||
        type.getIsHidden?.({
          tenant,
          flags,
          root,
        });

      return {
        ...type,
        isHidden,
        isDisabled,
        label: strings[type.id as keyof PlpFiltersMessagesProps] + labelAddon,
      };
    });

    useEffect(() => {
      if (filtersList.some(item => item.id.includes('monthly'))) {
        setPriceType('monthly-price');
      } else {
        setPriceType('price');
      }
    }, []);

    const clearAll = () => {
      const cantDeleteFilters: FilterType[] = filtersList.filter(fltr =>
        CANT_DELETE.includes(fltr.id),
      );

      setFiltersList(cantDeleteFilters);
      const newUrl = createPathFromFilters(cantDeleteFilters, root);
      router.push(newUrl);
    };
    const combinedStrings = { ...strings, ...dynamicStrings };
    const canClearAll = filtersList.filter(fltr => !CANT_DELETE.includes(fltr.id)).length;

    const areaFilter = filtersList.find(filter => filter.id === 'area');
    const regionFilter = filtersList.find(filter => filter.id === 'region');
    const dealershipFilter = filtersList.find(filter => filter.id === 'dealership-id');

    let filtersLength = mapFiltersForDisplay(filtersList).length;

    if (areaFilter) {
      filtersLength -= 3;
    } else if (regionFilter || dealershipFilter) {
      filtersLength -= 2;
    }

    return (
      <aside
        aria-label={strings.panelLabel}
        className={`${styles.filtersCol} ${isFiltersOpen ? styles.isOpen : ''}`}
        onClick={() => setIsFiltersOpen(false)}
      >
        <div
          className={`${styles.filtersWrapper} ${
            isShowingAllFilters ? styles.isShowingAllFilters : ''
          }`}
          onClick={(e: React.MouseEvent<HTMLElement>) => e.stopPropagation()}
        >
          <div className={styles.filtersHeader}>
            <Typography Component="p" variant="h5" className={styles.title}>
              {strings.heading}
              {filtersLength > 0 && ` (${filtersLength})`}
            </Typography>

            <button
              aria-label={strings.closeBtn}
              className={styles.closeBtn}
              data-test-id="close-filter-panel-btn"
              onClick={() => setIsFiltersOpen(false)}
            >
              <Close />
            </button>
            <Button
              className={styles.clearAllBtn}
              disabled={!canClearAll}
              data-test-id="clear-all-filters-btn"
              onClick={clearAll}
              variant="link"
              rightIcon={<Trash />}
            >
              {strings.clearAllBtn}
            </Button>
          </div>
          <div className={styles.filtersContent}>
            {children}

            {filterCategoriesIntl.map(category => {
              const { Component, id, isDisabled, isHidden, isMainFilter } = category;
              const AccordionComponent =
                id === 'make' || id === 'model' || id === 'variant_type'
                  ? SearchAccordion
                  : Accordion;

              if (isHidden) return null;

              return (
                <AccordionComponent
                  key={id}
                  className={`${styles.filterAccord} ${
                    !isMainFilter ? styles.secondaryFilter : ''
                  }`}
                  title={category.label || id}
                  open={!isDisabled ? category.defaultOpen : false}
                  disabled={isDisabled}
                  dataTestId={`${id}-filter`}
                  onExpandedChange={expanded =>
                    trackFilterEvent('features', expanded ? 'open' : 'close', filtersList)
                  }
                  renderWhenClosed
                >
                  <Component
                    strings={combinedStrings}
                    tenant={tenant}
                    isPlpFiltersSeoEnabled={enableFilterAnchors}
                  />
                </AccordionComponent>
              );
            })}
          </div>
          <div className={styles.mobileCtas}>
            <Button
              className={styles.clearBtnMob}
              onClick={clearAll}
              color={isRac ? 'primary' : 'secondary'}
              variant="outlined"
            >
              {strings.clearBtn}
            </Button>

            <Button
              className={styles.searchBtnMob}
              loading={filterUpdateStarted}
              onClick={() => setIsFiltersOpen(false)}
            >
              {dynamicStrings.showResultsBtn}
            </Button>
          </div>
        </div>

        <Button
          className={styles.viewMoreBtn}
          dataTestId="view-all-filters-toggle"
          leftIcon={<Filter />}
          onClick={() => setIsShowingAllFilters(!isShowingAllFilters)}
          color={isRac ? 'primary' : 'tertiary'}
          variant="outlined"
        >
          {isShowingAllFilters ? strings.viewLessFilters : strings.viewMoreFilters}
        </Button>
      </aside>
    );
  },
);

FilterPanel.displayName = 'FilterPanel';

/**
 * This code is essential for having the filter options render on the server
 *
 * A combination of `dangerouslyForceHydrate` and a manual tracking of whether it's been hydrated
 * via a ref and a `useEffect` is to ensure that new server-side values get hydrated, but only once
 * on the client-side.
 *
 * Otherwise, new SSR values don't get hydrated and the UI shows stale data even after a new session.
 */
const useInitialValues = ({
  initialAggregations,
  initialActiveFilters,
}: Pick<FilterPanelProps, 'initialActiveFilters' | 'initialAggregations'>) => {
  const hydrated = useRef(false);
  const aggregations = useMemo(
    (): AggregationsState =>
      initialAggregations
        ? { ...initialAggregations, isLoading: false, error: false }
        : initialAggregationsState,
    [initialAggregations],
  );

  useHydrateAtoms(
    // only hydrate the first time - on client side
    !hydrated.current
      ? [
          [filtersListAtom, initialActiveFilters ?? []],
          [aggregationsAtom, aggregations],
        ]
      : [],
    {
      dangerouslyForceHydrate: true,
    },
  );

  useEffect(() => {
    hydrated.current = true;
  }, []);
};
